import { EPTSessionType } from '@tableau/microview-communication-service/lib/src/channelDefinitions/SearchEPTChannelDefinitions';
import { SiteUrls } from '@tableau/shared-urls';
import { Location } from '../../commonInfra/utils/Location';
import { ContentFiltersKeys } from '../../spacesPlatform/models/ContentFilters';

import { QuickLinksTestId } from '../components/QuickLinks';
import { searchTelemetry } from '../components/SearchTelemetry';
import { ContentDefinition, ContentDefinitions } from '../models/ContentDefinitions';
import { ContentType, OmniBoxItemType } from '../models/Types';
import { OmniboxConfigurationService } from '../OmniboxConfigurationService';
import { SearchEPTService } from '../services/SearchEPTService';
import { SearchService } from '../services/SearchService';
import { IKeyboardNavigableItem, QuickSearchModule } from './QuickSearchModule';
import { BindUI } from '@tableau/react-mvvm';


export const ImpressionType = 'quickLinks';

export interface QuickLinkContentTypeItem extends IKeyboardNavigableItem {
    contentType: ContentType;
    url: string;
}

export interface QuickLinkOwnerItem extends IKeyboardNavigableItem {
    content: IUserInfo;
    url: string;
}

interface IIdItemInfo {
    id: string;
}

export interface IUserInfo extends IIdItemInfo {
    authId?: string;
    displayName: string;
    username: string;
    domainName?: string;
    readOnly?: boolean;
    userImageUrl?: string;
    isHiddenInternalUser?: boolean;
}

export class QuickLinksViewModel extends QuickSearchModule {
    @BindUI(QuickSearchModule.buildKeyboardNavigationPath) public contentTypeItems: QuickLinkContentTypeItem[];
    @BindUI() public areOwnersLoaded = false;
    @BindUI(QuickSearchModule.buildKeyboardNavigationPath) public ownerItems: QuickLinkOwnerItem[] = [];
    @BindUI() public currentKeyboardNavigableItem?: IKeyboardNavigableItem;

    public initialize(): void {
        const contentTypes = OmniboxConfigurationService.instance.configuration.contentTypesForQuickLinks.filter(contentType => ContentDefinitions[contentType].shouldBeAvailable());

        this.contentTypeItems = contentTypes.slice(0, 5).map((contentType, index) => {
            const contentDefinition = ContentDefinitions[contentType];
            return {
                content: contentDefinition,
                contentType: contentType,
                url: this.getContentTypeUrl(contentDefinition),
                ownerModule: this,
                getIdentifier: () => QuickLinksTestId + '-ContentType-' + index.toString()

            };
        });
    }

    public onMount() {
        super.onMount();
        if (OmniboxConfigurationService.instance.configuration.showOwnerLinks) {
            this.loadOwners();
        }

        if (!this.isUnmounted) {
            searchTelemetry.sendSearchImpressionEvent('', ImpressionType);
        }
    }

    public selectItemByKeyboard(): void {
        const contentTypeItem = this.currentKeyboardNavigableItem as QuickLinkContentTypeItem;
        const ownerItem = this.currentKeyboardNavigableItem as QuickLinkOwnerItem;

        if (this.contentTypeItems.indexOf(contentTypeItem) !== -1) {
            this.onContentTypeLinkClicked(contentTypeItem);
        } else {
            this.onOwnerItemClicked(ownerItem);
        }
    }

    public isKeyboardNavigable() {
        return !this.isUnmounted && (this.contentTypeItems?.length > 0 || this.ownerItems?.length > 0);
    }

    public onContentTypeLinkClicked(item: QuickLinkContentTypeItem) {
        this.omniboxViewModel.onResultItemSelected(OmniBoxItemType.contentTypeItem, this.contentTypeItems.length, this.contentTypeItems.indexOf(item), '-1', true);
        this.omniboxViewModel.closeSearch();
        SearchEPTService.instance.start(EPTSessionType.fullSearch, '');

        if (!OmniboxConfigurationService.instance.configuration.onContentTypeLinkClicked) {
            Location.instance.setUrl(item.url);
        } else {
            OmniboxConfigurationService.instance.configuration.onContentTypeLinkClicked(item.contentType);
        }
    }

    public onOwnerItemClicked(item: QuickLinkOwnerItem) {
        this.omniboxViewModel.onResultItemSelected(OmniBoxItemType.ownerItem, this.ownerItems.length, this.ownerItems.indexOf(item), '-1', true);
        this.omniboxViewModel.closeSearch();
        SearchEPTService.instance.start(EPTSessionType.fullSearch, '');

        if (!OmniboxConfigurationService.instance.configuration.onOwnerLinkClicked) {
            Location.instance.setUrl(item.url);
        } else {
            OmniboxConfigurationService.instance.configuration.onOwnerLinkClicked(item.content.id);
        }
    }

    public buildKeyboardNavigationPathInOwnModule() {

        for (let i = 0; i < this.contentTypeItems.length - 1; i++) {
            const currentItem = this.contentTypeItems[i];
            const nextItem = this.contentTypeItems[i + 1];
            this.linkTwoKeyboardNavigableItems(currentItem, nextItem, false);
            this.linkTwoKeyboardNavigableItems(currentItem, nextItem, true); // A11y requires up/down arrow to navigate horizontal section
        }

        this.contentTypeItems[0].up = this.previousKeyboardNavigableModule.getLastKeyboardNavigableItem();
        this.contentTypeItems[this.contentTypeItems.length - 1].down = this.nextKeyboardNavigableModule.getFirstKeyboardNavigableItem();
    }

    public getFirstKeyboardNavigableItem(): IKeyboardNavigableItem {
        return this.contentTypeItems[0];
    }

    public getLastKeyboardNavigableItem(): IKeyboardNavigableItem {
        return this.contentTypeItems[this.contentTypeItems.length - 1];
    }

    public shouldShowOwnerList() {
        return (OmniboxConfigurationService.instance.configuration.showOwnerLinks) && !SearchService.instance.Server.isLimitedUserVisibilityChannel();
    }

    public loadOwners() {
        this.areOwnersLoaded = false;
        this.ownerItems = [];

        if (!SearchService.instance.Server.isLimitedUserVisibilityChannel()) {
            SearchService.instance.getRelatedOwners().then(result => {
                //
                //  This timeout is (unfortunately) required to have 2 renders:
                //  1. Render the removal of loading timer, to be replaced with the owner section (showPending = False)
                //  2. Render the owner section with results.
                //  This creates the conditions for the animation to occur because the height of that section
                //  goes from X pixels to 0, triggering the transition.
                //
                setTimeout(() => {
                    const owners = [];
                    const ownerMap = {};
                    result.every(item => {
                        if (item.content.ownerId && !ownerMap[item.content.ownerId]) {
                            owners.push(item.content);
                            ownerMap[item.content.ownerId] = true;
                        }
                        return owners.length < 5;
                    });

                    SearchService.instance.usersWithProfilePictures(owners.map(u => u.ownerId)).then(ownersWithProfilePicture => {
                        this.areOwnersLoaded = true;
                        this.ownerItems = owners.map((owner, index) => {
                            return {
                                content: ownersWithProfilePicture.items.find(ownerWithProfilePicture => owner.ownerId === ownerWithProfilePicture.id) || {
                                    id: owner.ownerId,
                                    displayName: owner.ownerName
                                },
                                url: this.getUserFilteredSearchUrl(owner.ownerId),
                                ownerModule: this,
                                getIdentifier: () => QuickLinksTestId + '-Owner-' + index.toString()
                            };
                        });
                    });
                });
            });
        } else {
            this.areOwnersLoaded = true;
        }
    }

    private getUserFilteredSearchUrl(ownerId: string): string {
        const urls = new SiteUrls({ siteUrlName: SearchService.instance.Server.sessionSiteUrlName(), queryString: window.location.search });
        return urls.searchUrl() + '?' + ContentFiltersKeys.owner + '=' + ownerId + '&' + ContentFiltersKeys.search + '=';
    }

    private getContentTypeUrl(content: ContentDefinition): string {
        const urls = new SiteUrls({ siteUrlName: SearchService.instance.Server.sessionSiteUrlName(), queryString: window.location.search });
        return content.getSearchUrl(urls);
    }
}